import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";

const TEXT_ONLY_TAGS = new Set([
  "a",
  "b",
  "br",
  "code",
  "div",
  "em",
  "i",
  "li",
  "mark",
  "ol",
  "p",
  "small",
  "span",
  "strong",
  "u",
  "ul",
]);

const flattenNodeText = (node) => {
  if (node == null || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(flattenNodeText).join("\n");
  }

  if (!React.isValidElement(node)) {
    return "";
  }

  if (node.type === React.Fragment) {
    return flattenNodeText(node.props?.children);
  }

  if (node.type === "br") {
    return "\n";
  }

  const innerText = flattenNodeText(node.props?.children);

  if (node.type === "li") {
    return `\n${innerText}`;
  }

  if (
    node.type === "p" ||
    node.type === "div" ||
    node.type === "ul" ||
    node.type === "ol"
  ) {
    return `${innerText}\n`;
  }

  return innerText;
};

const hasStructuredDetailContent = (node) => {
  if (node == null || typeof node === "boolean") {
    return false;
  }

  if (typeof node === "string" || typeof node === "number") {
    return false;
  }

  if (Array.isArray(node)) {
    return node.some(hasStructuredDetailContent);
  }

  if (!React.isValidElement(node)) {
    return false;
  }

  if (node.type === React.Fragment) {
    return hasStructuredDetailContent(node.props?.children);
  }

  if (typeof node.type === "string") {
    if (TEXT_ONLY_TAGS.has(node.type)) {
      return hasStructuredDetailContent(node.props?.children);
    }

    return true;
  }

  return true;
};

const normalizeStepLine = (line) =>
  line
    .replace(/^[-*•]\s*/, "")
    .replace(/^step\s*\d+\s*[:.)-]?\s*/i, "")
    .replace(/^\d+\s*[:.)-]\s*/, "")
    .trim();

export const ThinkingIndicator = ({ label = "Thinking" }) => (
  <span className="ea-thinking" role="status" aria-live="polite">
    <span className="ea-thinking__orb" aria-hidden="true" />
    <span className="ea-glow-text">{label}</span>
  </span>
);

const StepTimeline = ({ steps, offset = 0, activeIndex = -1 }) => (
  <ol className="ea-analysis-steps__list">
    {steps.map((step, index) => {
      const absoluteIndex = offset + index;
      const isActive = absoluteIndex === activeIndex;

      return (
        <li
          className={`ea-analysis-steps__list-item ${
            isActive ? "is-active" : "is-done"
          }`}
          key={`${absoluteIndex}-${step}`}
        >
          <span className="ea-analysis-steps__node" aria-hidden="true" />
          <span
            className={`ea-analysis-steps__list-copy ${
              isActive ? "ea-glow-text" : ""
            }`}
          >
            {step}
          </span>
        </li>
      );
    })}
  </ol>
);

const formatElapsed = (ms) => {
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

// Tracks how long the block has been streaming; freezes once it stops.
const useElapsedWhileStreaming = (isStreaming) => {
  const startedAtRef = useRef(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!isStreaming) return undefined;
    if (startedAtRef.current == null) startedAtRef.current = Date.now();

    const tick = () => setElapsedMs(Date.now() - startedAtRef.current);
    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [isStreaming]);

  return startedAtRef.current == null ? null : elapsedMs;
};

// `footer` renders inside the expanded body below the steps (e.g. the
// answer's source chips). The `inline` variant is a compact status line with
// an elapsed timer that expands into a bordered step list.
const CollapsibleDiv = ({
  children,
  isStreaming,
  footer = null,
  variant = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isInline = variant === "inline";
  const elapsedMs = useElapsedWhileStreaming(isStreaming);

  const stepLines = useMemo(() => {
    const rawText = flattenNodeText(children);
    if (!rawText) {
      return [];
    }

    return rawText
      .split(/\r?\n/)
      .map(normalizeStepLine)
      .filter(Boolean)
      .filter((line) => !/^analysis steps$/i.test(line));
  }, [children]);

  const hasDetailContent = useMemo(
    () => hasStructuredDetailContent(children),
    [children],
  );
  const stepCount = stepLines.length;
  const activeIndex = isStreaming ? stepCount - 1 : -1;
  // Collapsed by default. While streaming, the header itself carries the
  // current step so progress stays visible without expanding.
  const currentStep = isStreaming ? stepLines[stepCount - 1] : "";
  const title = isStreaming
    ? currentStep || "Analyzing"
    : isOpen
      ? "Hide analysis steps"
      : "See analysis steps";

  if (isInline) {
    const inlineTitle = isStreaming
      ? currentStep || "Thinking"
      : isOpen
        ? "Hide analysis steps"
        : "Analysis steps";

    return (
      <section
        className={`ea-analysis-steps ea-analysis-steps--inline ${
          isOpen ? "is-open" : ""
        } ${isStreaming ? "is-streaming" : ""}`}
      >
        <button
          type="button"
          className="ea-analysis-steps__toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          <span className="ea-analysis-steps__dots" aria-hidden="true">
            <span />
            <span />
          </span>
          <span
            className={`ea-analysis-steps__title ${
              isStreaming ? "ea-glow-text" : ""
            }`}
            aria-live={isStreaming ? "polite" : undefined}
          >
            {inlineTitle}
          </span>
          {elapsedMs != null && (
            <span className="ea-analysis-steps__elapsed">
              {formatElapsed(elapsedMs)}
            </span>
          )}
          <span className="ea-analysis-steps__chevron" aria-hidden="true">
            <ChevronRight size={16} />
          </span>
        </button>

        {isOpen && (
          <div className="ea-analysis-steps__body">
            {stepCount > 0 && (
              <ol className="ea-analysis-steps__rows">
                {stepLines.map((step, index) => (
                  <li
                    key={`${index}-${step}`}
                    className={index === activeIndex ? "is-active" : ""}
                  >
                    {step}
                  </li>
                ))}
              </ol>
            )}
            {(hasDetailContent || stepCount === 0) && (
              <div className="ea-analysis-steps__detail">
                <div className="ea-analysis-steps__detail-content">{children}</div>
              </div>
            )}
            {footer && <div className="ea-analysis-steps__footer">{footer}</div>}
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      className={`ea-analysis-steps ${isOpen ? "is-open" : ""} ${
        isStreaming ? "is-streaming" : ""
      }`}
    >
      <button
        type="button"
        className="ea-analysis-steps__toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="ea-analysis-steps__orb" aria-hidden="true" />
        <span
          className={`ea-analysis-steps__title ${
            isStreaming ? "ea-glow-text" : ""
          }`}
          aria-live={isStreaming ? "polite" : undefined}
        >
          {title}
        </span>
        {stepCount > 0 && (
          <span className="ea-analysis-steps__meta">
            {stepCount} {stepCount === 1 ? "step" : "steps"}
          </span>
        )}
        <span className="ea-analysis-steps__chevron" aria-hidden="true">
          <ChevronRight size={16} />
        </span>
      </button>

      {isOpen && (
        <div className="ea-analysis-steps__body">
          {stepCount > 0 && (
            <StepTimeline steps={stepLines} activeIndex={activeIndex} />
          )}

          {(hasDetailContent || stepCount === 0) && (
            <div className="ea-analysis-steps__detail">
              <div className="ea-analysis-steps__detail-content">{children}</div>
            </div>
          )}
          {footer && <div className="ea-analysis-steps__footer">{footer}</div>}
        </div>
      )}
    </section>
  );
};

export default CollapsibleDiv;
